import parsePhoneNumberFromString, { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

import { marketingDataSchema } from "@/schemas/validation/marketingDataSchema";

// Type for validation message translator (e.g. from useTranslations("Finance.form.validation"))
export type FinanceValidationTranslator = (key: string) => string;

const DEFAULT_MESSAGES = {
  selectOption: "Please select an option",
  nameMin: "Name must be at least 2 characters",
  nameMax: "Name must be less than 50 characters",
  salaryRequired: "Please enter your current salary",
  phoneRequired: "Phone number is required",
  phoneInvalid: "Please enter a valid phone number",
  required: "Required",
};

const getMessages = (t?: FinanceValidationTranslator): typeof DEFAULT_MESSAGES =>
  t
    ? (Object.fromEntries(
        (Object.keys(DEFAULT_MESSAGES) as (keyof typeof DEFAULT_MESSAGES)[]).map((k) => [k, t(k)]),
      ) as typeof DEFAULT_MESSAGES)
    : DEFAULT_MESSAGES;

/** Creates step1Schema with optional translated validation messages */
export const createStep1Schema = (t?: FinanceValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    existingLoans: z.enum(["yes", "no"], { required_error: m.selectOption }),
    hasCreditScore: z.enum(["yes", "no"], { required_error: m.selectOption }),
    totalMonthlyInstallments: z.number().default(0),
    totalCreditLimit: z.number().default(0),
    previousDisputes: z.string({ required_error: m.selectOption }),
    marketing: marketingDataSchema,
  });
};

/** Creates step2Schema with optional translated validation messages */
export const createStep2Schema = (t?: FinanceValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    salaryType: z.string({ required_error: m.selectOption }),
    isCompanyOlderThanOneYear: z.string({ required_error: m.selectOption }),
  });
};

export const step3Schema = z.object({});

/** Creates step4Schema with optional translated validation messages */
export const createStep4Schema = (t?: FinanceValidationTranslator) => {
  const m = getMessages(t);
  return z.object({
    fullName: z.string({ required_error: m.nameMin }).min(2, m.nameMin).max(50, m.nameMax),
    email: z.string().email().optional(),
    currentSalary: z.number({ required_error: m.required }).min(1, m.salaryRequired),
    contactNumber: z
      .string({ required_error: m.phoneRequired })
      .min(1, m.phoneRequired)
      .refine((value) => {
        if (!value) return false;
        try {
          const isValid = isValidPhoneNumber(value);
          if (!isValid) return false;
          const phoneNumber = parsePhoneNumberFromString(value);
          return phoneNumber?.isValid() ?? false;
        } catch {
          return false;
        }
      }, m.phoneInvalid),
  });
};

/** Default schemas (English) for backward compatibility */
export const step1Schema = createStep1Schema();
export const step2Schema = createStep2Schema();
export const step4Schema = createStep4Schema();

/** Creates full financeFormSchema with optional translated validation messages */
export const createFinanceFormSchema = (t?: FinanceValidationTranslator) =>
  createStep1Schema(t).merge(createStep2Schema(t)).merge(step3Schema).merge(createStep4Schema(t));

// Combined schema for the entire form
export const financeFormSchema = createFinanceFormSchema();

// Type definitions for each step
export type Step1Data = z.infer<ReturnType<typeof createStep1Schema>>;
export type Step2Data = z.infer<ReturnType<typeof createStep2Schema>>;
export type Step3Data = z.infer<typeof step3Schema>;
export type Step4Data = z.infer<ReturnType<typeof createStep4Schema>>;

// Type definition for the complete form
export type FinanceFormData = z.infer<ReturnType<typeof createFinanceFormSchema>>;

// Helper function to get step schema
export const getStepSchema = (step: number, t?: FinanceValidationTranslator) => {
  switch (step) {
    case 1:
      return createStep1Schema(t);
    case 2:
      return createStep2Schema(t);
    case 3:
      return step3Schema;
    case 4:
      return createStep4Schema(t);
    default:
      return createStep1Schema(t);
  }
};
